// [GLB 실패 대비 대체본] 보스 그레이박스 '고목 수호자': 줄기·뿌리(병합) + 수관(병합) + 눈 + 팔 2개. 세계 시계로 자세를 만든다.
// 등장 무적(BossView.invulnerable)은 옅은 금빛 림 맥동으로 보여 준다(1.4Hz, 화면 섬광 아님).
import * as THREE from 'three/webgpu';
import { float, floor, mix, screenCoordinate, uniform } from 'three/tsl';
import { BOSS } from '../../shared/config';
import { BossState, type GameView } from '../../shared/types';
import type { GameClocks } from '../clock';
import { colored, merge } from '../geo';
import { addHull, charMaterial, hullMaterial, RimControl, type EmissiveMat } from '../materials';
import { clamp01, damp, easeIn, easeInOut, easeOut, lerp, phaseOf } from '../util';

/** 상태별 연출 길이(틱). 게임 쪽 타이밍과 다르면 clamp되어 마지막 자세를 유지한다. */
const T = { intro: 90, slamWindup: 72, slam: 18, sweepWindup: 60, sweep: 22, summon: 60, dying: 120 } as const;

export class BossGraybox {
  readonly root = new THREE.Group();
  readonly rim = new RimControl(0xfff0c8, 0.3);
  private readonly lean = new THREE.Group();
  private readonly armL: THREE.Group = new THREE.Group();
  private readonly armR: THREE.Group = new THREE.Group();
  private readonly crown: THREE.Mesh;
  private readonly mats: EmissiveMat[] = [];
  private readonly hullMat = hullMaterial(0.07);
  /** 영웅이 보스 뒤(화면상 수관·줄기 아래)에 있을 때 보스를 1px 체크무늬로 비춰 보이게 하는 정도 0..1 */
  private readonly fade = uniform(0);
  private fadeV = 0;
  private readonly geos: THREE.BufferGeometry[] = [];
  private armLX = 0; private armRX = 0; private armLZ = 0; private armRZ = 0;
  private yawOff = 0;
  private leanX = 0;
  private yOff = 0;
  private sy = 1;
  x = 0; z = 0;

  constructor() {
    const barkMat = charMaterial(0xffffff, this.rim, { roughness: 1, vertexColors: true });
    const leafMat = charMaterial(0xffffff, this.rim, { roughness: 1, vertexColors: true });
    const eyeMat = new THREE.MeshStandardMaterial({ color: 0xffb040, emissive: new THREE.Color(0xff8a20), emissiveIntensity: 2.2 });
    this.mats.push(barkMat, leafMat);
    // 디더 반투명: 불투명 파이프라인 그대로 픽셀 절반을 버린다(정렬 문제 없음)
    const checker = floor(screenCoordinate.x).add(floor(screenCoordinate.y)).mod(2);
    for (const mt of [barkMat, leafMat, this.hullMat] as THREE.NodeMaterial[]) {
      mt.opacityNode = mix(float(1), checker, this.fade);
      mt.alphaTest = 0.5;
    }

    const roots: THREE.BufferGeometry[] = [colored(new THREE.CylinderGeometry(1.7, 2.2, 6.6, 12), 0x5b4130, 0, 3.3, 0)];
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + 0.3;
      const g = new THREE.BoxGeometry(0.8, 0.7, 2.4);
      g.rotateY(a);
      roots.push(colored(g, 0x4e3828, Math.sin(a) * 2.1, 0.35, Math.cos(a) * 2.1));
    }
    const trunkGeo = merge(roots);
    const trunk = new THREE.Mesh(trunkGeo, barkMat);
    trunk.castShadow = true;
    addHull(trunk, this.hullMat);

    const crownGeo = merge([
      colored(new THREE.SphereGeometry(3.4, 14, 12), 0x3f7a3a),
      colored(new THREE.SphereGeometry(2.3, 12, 10), 0x4a8a40, 2.4, -0.5, 0.6),
      colored(new THREE.SphereGeometry(2.1, 12, 10), 0x376d33, -2.5, -0.4, -0.5),
      colored(new THREE.SphereGeometry(1.9, 12, 10), 0x4f9244, 0.2, 1.6, -1.6),
    ]);
    this.crown = new THREE.Mesh(crownGeo, leafMat);
    this.crown.position.set(0, 8.2, 0);
    this.crown.castShadow = true;
    addHull(this.crown, this.hullMat);

    const eyeL = new THREE.BoxGeometry(0.55, 0.28, 0.2);
    eyeL.rotateZ(0.35);
    eyeL.translate(-0.7, 5.6, 1.62);
    const eyeR = new THREE.BoxGeometry(0.55, 0.28, 0.2);
    eyeR.rotateZ(-0.35);
    eyeR.translate(0.7, 5.6, 1.62);
    const eyes = new THREE.Mesh(merge([eyeL, eyeR]), eyeMat);

    const armGeo = merge([
      colored(new THREE.CylinderGeometry(0.55, 0.7, 4.4, 8), 0x5b4130, 0, -2.2, 0),
      colored(new THREE.SphereGeometry(0.95, 10, 8), 0x4e3828, 0, -4.5, 0),
    ]);
    for (const [arm, sx] of [[this.armL, -1], [this.armR, 1]] as const) {
      arm.position.set(sx * 2.1, 5.7, 0.2);
      const m = new THREE.Mesh(armGeo, barkMat);
      m.castShadow = true;
      addHull(m, this.hullMat);
      arm.add(m);
    }
    this.geos.push(trunkGeo, crownGeo, armGeo, eyes.geometry);
    this.lean.add(trunk, this.crown, eyes, this.armL, this.armR);
    this.root.add(this.lean);
    this.root.visible = false;
    this.mats.push(eyeMat);
  }

  update(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const b = view.boss;
    const st = b.state;
    const show = st !== BossState.Inactive && st !== BossState.Dead;
    this.root.visible = show;
    if (!show) return;
    this.x = b.prevX + (b.x - b.prevX) * alpha;
    this.z = b.prevZ + (b.z - b.prevZ) * alpha;
    const t = phaseOf(b.stateTick, clocks.worldRate, alpha);

    let aLX = 0.15, aRX = 0.15, aLZ = -0.2, aRZ = 0.2; // 팔 아래로 늘어짐
    let yaw = 0, lean = 0, yOff = 0, sy = 1, crownPulse = 1;
    let sharp = 8;
    switch (st) {
      case BossState.Intro: {
        const p = clamp01(t / T.intro);
        yOff = -7.5 * (1 - easeOut(p)); // 땅에서 솟아오른다
        aLX = aRX = lerp(1.2, 0.15, easeInOut(p));
        sharp = 0;
        break;
      }
      case BossState.Chase: {
        const ph = clocks.worldNow * 0.12;
        lean = 0.06; yOff = Math.abs(Math.sin(ph)) * 0.18;
        aLX = 0.4 + Math.sin(ph) * 0.4; aRX = 0.4 - Math.sin(ph) * 0.4;
        break;
      }
      case BossState.SlamWindup: {
        const p = easeOut(clamp01(t / T.slamWindup));
        aLX = aRX = lerp(0.15, -3.0, p); // 양팔을 머리 위로
        aLZ = -0.3; aRZ = 0.3;
        lean = -0.16 * p; sy = 1 + 0.04 * p;
        sharp = 0;
        break;
      }
      case BossState.Slam: {
        const p = clamp01(t / T.slam);
        aLX = aRX = lerp(-3.0, -0.35, easeIn(Math.min(1, p * 2.2)));
        lean = 0.3 * Math.min(1, p * 3) * (1 - clamp01((p - 0.6) / 0.4) * 0.6);
        sy = 1 - 0.07 * (p < 0.5 ? p * 2 : 2 - p * 2); // 착지 순간 눌림
        sharp = 0;
        break;
      }
      case BossState.SweepWindup: {
        const p = easeOut(clamp01(t / T.sweepWindup));
        aLZ = lerp(-0.2, -1.55, p); aRZ = lerp(0.2, 1.55, p);
        aLX = aRX = lerp(0.15, 0.5, p);
        yaw = -1.0 * p;
        lean = -0.08 * p;
        sharp = 0;
        break;
      }
      case BossState.Sweep: {
        const p = clamp01(t / T.sweep);
        aLZ = -1.55; aRZ = 1.55; aLX = aRX = 0.5;
        yaw = lerp(-1.0, 1.0, easeOut(p));
        lean = 0.12;
        sharp = 0;
        break;
      }
      case BossState.Summon: {
        const p = clamp01(t / T.summon);
        aLX = aRX = lerp(0.15, -2.7, easeOut(Math.min(1, p * 3)));
        aLZ = -0.5; aRZ = 0.5;
        crownPulse = 1 + 0.08 * Math.sin(t * 0.5);
        break;
      }
      case BossState.Stagger:
        lean = -0.3 + Math.sin(t * 0.6) * 0.03;
        aLX = aRX = 0.6; aLZ = -0.9; aRZ = 0.9;
        sharp = 14;
        break;
      case BossState.Dying: {
        const p = clamp01(t / T.dying);
        lean = -1.4 * easeIn(p);
        yOff = -1.5 * p;
        aLX = aRX = 0.8; aLZ = -1.0; aRZ = 1.0;
        sharp = 0;
        break;
      }
      default:
        break;
    }
    const k = sharp === 0 ? 1 : damp(sharp, dt);
    this.armLX += (aLX - this.armLX) * k; this.armRX += (aRX - this.armRX) * k;
    this.armLZ += (aLZ - this.armLZ) * k; this.armRZ += (aRZ - this.armRZ) * k;
    this.yawOff += (yaw - this.yawOff) * k;
    this.leanX += (lean - this.leanX) * k;
    this.yOff += (yOff - this.yOff) * k;
    this.sy += (sy - this.sy) * k;

    this.root.position.set(this.x, this.yOff, this.z);
    this.root.rotation.y = b.facing + this.yawOff;
    this.lean.rotation.x = this.leanX;
    this.lean.scale.set(1, this.sy, 1);
    this.armL.rotation.set(this.armLX, 0, this.armLZ);
    this.armR.rotation.set(this.armRX, 0, this.armRZ);
    this.crown.scale.setScalar(crownPulse);

    // 영웅이 보스 북쪽(카메라 반대편) 가까이 있어 수관·줄기에 가리면 보스를 비춰 보이게 한다
    const h = view.hero;
    const hx = h.prevX + (h.x - h.prevX) * alpha, hz = h.prevZ + (h.z - h.prevZ) * alpha;
    const dz = this.z - hz, dx = Math.abs(this.x - hx);
    const behind = dz > -1.5 && dz < 10 && dx < 5.5 ? 1 : 0;
    this.fadeV += (behind - this.fadeV) * damp(10, dt);
    this.fade.value = this.fadeV > 0.02 ? this.fadeV : 0;

    // 피격 플래시(국소, 약하게: 연속 타격에서 블룸과 겹쳐 하얗게 날아가지 않도록), 무적이면 금빛 림 맥동
    const flash = b.hitTick < 4;
    for (let i = 0; i < 2; i++) {
      this.mats[i].emissive.setHex(flash ? 0xffffff : 0x000000);
      this.mats[i].emissiveIntensity = flash ? 0.16 : 0;
    }
    if (b.invulnerable === true) {
      this.rim.color.value.setHex(0xffd36a);
      this.rim.strength.value = 0.9 + 0.5 * Math.sin(clocks.realMs * 0.0088);
    } else {
      this.rim.color.value.setHex(0xfff0c8);
      this.rim.strength.value = 0.3;
    }
  }

  /** 문서 대조용: 설계 반경 */
  static readonly radius = BOSS.radius;

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.hullMat.dispose();
  }
}
